import { postApiGalaxyServer } from 'services/api';
import { MergeExclusive } from 'type-fest';
import ApiClientManager from 'core/apiClient';

type SharedProvisionIntegrationPayload = {
  mosaicTeamId: number;
  mosaicTeamName: string;
  targetService: string;
  agentUrl?: string;
  serverUrl: string;
  /**
   * Optional tenant for API header override.
   * Used to set x-tenant-name header for multi-tenant routing.
   */
  tenant?: string;
  /**
   * Optional realmId for API header override.
   * Used to set x-realm-id header for multi-tenant routing.
   * Note: This is separate from nested paramsOnGalaxy.realmId which is part of the request body.
   */
  realmId?: string;
  integrationSetting?: {
    defaultBoardId?: number;
    bakIntegration?: boolean;
    sendToAgent?: { time_entry: boolean };
  };
  paramsOnAgent?: {
    forceCreate?: boolean;
    serviceIndex?: number;
    serviceName?: string;
    credentials?: {
      username?: string;
      password?: string;
      database?: string;
      serverApiKey?: string;
    };
    settings?: {
      ConsultFee: boolean;
      FeeDirLab?: boolean;
      phase_FeeDirLab?: boolean;
    };
    paramsOnServer?: {
      url?: string;
      credentials?: {
        username?: string;
        password?: string;
        database?: string;
      };
      params?: {
        agentDomain: string;
        credentials?: {
          username?: string;
          password?: string;
          database?: string;
        };
      };
      paramsOnGalaxy?: {
        s3BucketName?: string;
        realmId?: string;
      };
    };
  };
  paramsOnGalaxy?: {
    s3BucketName?: string;
    realmId?: string;
  };
  paramsOnServer?: {
    url?: string;
    params?: {
      agentDomain?: string;
      credentials?: {
        username?: string;
        password?: string;
        database?: string;
      };
    };
  };
};

type ProvisionVisionPayload = Omit<
  SharedProvisionIntegrationPayload,
  'paramsOnAgent' | 'paramsOnGalaxy' | 'paramsOnServer'
> & {
  paramsOnAgent: {
    forceCreate?: boolean;
    credentials: {
      serverApiKey: string;
    };
    humanFriendlySetting: {
      agentSettings: { companyList: number[] };
    };
    settings: {
      ConsultFee: boolean;
      FeeDirLab: boolean;
      phase_FeeDirLab: boolean;
    };
    paramsOnServer: {
      url: string;
      params: {
        agentDomain: string;
        credentials: {
          username: string;
          password: string;
          database: string;
        };
      };
    };
    paramsOnGalaxy: {
      s3BucketName: string;
      realmId?: string;
    };
  };
};

export type ProvisionIntegrationPayload = MergeExclusive<
  SharedProvisionIntegrationPayload,
  ProvisionVisionPayload
>;

export type ProvisionIntegrationSuccessResponse = {
  targetServiceId: number;
};

export type ProvisionIntegrationErrorResponse = {
  error: string;
  message: string;
  statusCode: number;
};

export type ProvisionError = {
  id: number;
  createdAt: string;
  sessionId: number;
  payload: Record<string, unknown>;
  response: {
    error: '';
  };
  isArray: boolean;
  mosaicTeamId: number;
  origin: string;
  target: string;
  targetServiceId: number;
};

export type ProvisionErrors = ProvisionError[];

export const provisionIntegrationApi = ({
  params,
  token,
  config = {},
}: BaseApiParams<ProvisionIntegrationPayload>) => {
  // Build custom headers if tenant/realmId are provided to override global cache
  const customHeaders =
    params.tenant && params.realmId
      ? ApiClientManager.buildUrlHeaders({ tenantName: params.tenant, realmId: params.realmId })
      : undefined;

  // Remove header-only fields from the request body (they shouldn't be sent to the API)
  const { tenant: _tenant, realmId: _realmId, ...bodyParams } = params;

  return postApiGalaxyServer(
    `/integrations_api/provision`,
    bodyParams,
    { ...config, ...(customHeaders && { headers: customHeaders }) },
    token,
  );
};
